import { Box, Card, Stack, Typography } from '@mui/material';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import { useThemeMode } from '../../theme/ThemeModeContext';
import { DELTA_DOWN, DELTA_UP } from './charts/chartTokens';

type Props = {
  icon: ComponentType<SvgIconProps>;
  label: string;
  value: string | number;
  deltaPct?: number | null;
  deltaLabel?: string;
  color?: string;
  onClick?: () => void;
};

const StatCard = ({ icon: Icon, label, value, deltaPct, deltaLabel, color = '#6226A7', onClick }: Props) => {
  const { mode } = useThemeMode();
  const hasDelta = typeof deltaPct === 'number' && !Number.isNaN(deltaPct);
  const isUp = hasDelta && (deltaPct as number) >= 0;
  const deltaColor = isUp ? DELTA_UP[mode] : DELTA_DOWN[mode];

  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2.5,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        background: (theme) =>
          theme.palette.mode === 'light'
            ? `linear-gradient(135deg, ${color}1F 0%, #ffffff 55%)`
            : `linear-gradient(135deg, ${color}33 0%, ${theme.palette.background.paper} 60%)`,
        '&::before': {
          content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
          background: `linear-gradient(180deg, ${color}, ${color}66)`,
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '16px',
            display: 'grid',
            placeItems: 'center',
            background: `linear-gradient(135deg, ${color}, ${color}B3)`,
            boxShadow: `0 8px 18px ${color}55, inset 0 1px 0 rgba(255,255,255,.45)`,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
      </Stack>

      {hasDelta && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1.5 }}>
          {isUp ? (
            <ArrowUpwardRounded sx={{ fontSize: 16, color: deltaColor }} />
          ) : (
            <ArrowDownwardRounded sx={{ fontSize: 16, color: deltaColor }} />
          )}
          <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 700 }}>
            {Math.abs(deltaPct as number).toFixed(0)}%
          </Typography>
          {deltaLabel && (
            <Typography variant="caption" color="text.secondary">
              {deltaLabel}
            </Typography>
          )}
        </Stack>
      )}
    </Card>
  );
};

export default StatCard;
